// HU-50 · HU-24 · La bandeja de Asuntos Regulatorios y el trámite ante la autoridad
// (F-05, Sprint 2).
//
// LA BANDEJA PRIORIZA, NO ESCONDE (RN-35). El servidor devuelve los bloques YA clasificados
// y ordenados —Vencidos, Por vencer, En plazo, Esperando respuesta de afuera, Informe final
// pendiente, y aparte «Sin plazo configurado» (C10)— y esta pantalla los pinta tal cual.
// Los relojes son el PLAZO REGULATORIO (RN-20/21) calculado por el backend con el
// calendario hábil de cada país y los parámetros de HU-23; un front que recalculara tendría
// dos verdades. Dos relojes por fila: la notificación y, en los graves, el informe final
// (RN-39, HU-24-C10).
//
// CON ÁMBITO POR PAÍS (RNF-08, C19): el backend acota la bandeja y «Ver todos los casos» a
// los países del actor. Aquí no se filtra por país porque no hay nada que filtrar.

import { pedir, pedirBinario, segUuid } from './cliente';

export type EstadoDelReloj = 'en_plazo' | 'por_vencer' | 'vencido' | 'cumplido' | 'sin_plazo_configurado';
export type ClaveDeBloqueRegulatorio =
  | 'vencidos' | 'por_vencer' | 'en_plazo' | 'esperando_afuera' | 'informe_final_pendiente' | 'sin_plazo_configurado';
export type AccionRegulatoria =
  | 'completar_formato_amarillo' | 'preparar_la_carta' | 'solicitar_la_firma' | 'esperar_la_firma'
  | 'presentar_la_carta' | 'registrar_el_cargo' | 'remitir_el_informe_final';

/** Un reloj en la fila: estado + texto (RNF-11) + la fecha límite. */
export type RelojDeBandeja = {
  estado: EstadoDelReloj;
  texto: string;
  fecha_limite: string | null;
  calculo_fiable: boolean;
  advertencia: string | null;
};

/** C6: las ocho columnas, más lo que hace falta para pintar la fila. */
export type FilaRegulatoria = {
  caso_id: string;
  numero: string | null;
  pais: string;
  nombre_pais: string;
  autoridad: string | null;
  producto: string | null;
  fecha_recepcion: string;
  gravedad: 'grave' | 'no_grave' | null;
  notificacion: RelojDeBandeja;
  /** RN-39: solo los graves lo tienen. */
  informe_final: RelojDeBandeja | null;
  accion: AccionRegulatoria | null;
  texto_de_la_accion: string | null;
  /** C12: pasados N días hábiles desde la carta enviada y sin cargo. */
  acuse_demorado: boolean;
  /** HU-22-C2/C3 · BUG-O11: «Acuse pendiente» · «Acuse demorado» · «Cargo recibido · dd/mm/aaaa». */
  texto_del_acuse: string | null;
  estado_carta: string;
  estado_cargo: string;
  /** HU-21-C8: días hábiles esperando la firma del PRFV; null si no se espera. */
  dias_esperando_firma: number | null;
  /**
   * CC-02 (B10, AT S3 §11.3.8): una fila por TRÁMITE. `autoridad` lista los destinos
   * («SENASA · SANIPES»); los relojes, el estado y el paso son los de la carta más urgente
   * (`autoridad_codigo`); `texto_de_mas_cartas` es el «y 1 carta más» del paso y `cartas` el
   * sub-estado de cada carta. Opcionales: un servidor anterior a CC-02 no los trae.
   */
  autoridad_codigo?: string | null;
  texto_de_mas_cartas?: string | null;
  /** RN-81: «Usa el plazo de SENASA: falta el de SANIPES», de la carta más urgente. */
  plazo_prestado_de?: string | null;
  texto_de_plazo_prestado?: string | null;
  cartas?: CartaDeLaFila[];
};

/** CC-02 (B10): una carta dentro de la fila del trámite, con lo que el servidor deriva de ella. */
export type CartaDeLaFila = {
  autoridad_codigo: string | null;
  autoridad: string | null;
  /** El sub-estado tal cual: «SENASA: enviada, cargo pendiente» (CC2-50-C1). */
  texto: string;
  estado_carta: string;
  estado_cargo: string;
  notificacion: RelojDeBandeja;
  informe_final: RelojDeBandeja | null;
  accion: AccionRegulatoria | null;
  texto_de_la_accion: string | null;
  acuse_demorado: boolean;
  texto_del_acuse: string | null;
  /** HU-21-C8 por carta: los días hábiles que ESTA carta lleva esperando la firma del PRFV. */
  dias_esperando_firma: number | null;
  plazo_prestado_de: string | null;
  /** RN-81: «Usa el plazo de SENASA: falta el de SANIPES» (CC2-24-C2). */
  texto_de_plazo_prestado: string | null;
};

export type BloqueRegulatorio = {
  clave: ClaveDeBloqueRegulatorio;
  titulo: string;
  orden: number;
  cuenta: number;
  filas: FilaRegulatoria[];
};

export type BandejaRegulatoria = {
  bloques: BloqueRegulatorio[];
  total: number;
  filtrado_por: string | null;
  mensaje_de_vacia: string | null;
  /** C14: las autoridades presentes en el ámbito, para el filtro. */
  autoridades: string[];
  consultada_en: string;
  /** Casos con trámite pendiente que no entraron por el tope de la consulta (0 en la operación normal). */
  candidatos_fuera_del_tope?: number;
};

export type FiltrosRegulatorios = {
  autoridad?: string;
  accion?: AccionRegulatoria | '';
  estado_plazo?: EstadoDelReloj | '';
  producto?: string;
  desde?: string;
  hasta?: string;
  numero?: string;
};

/** C20: un caso del ámbito, esté o no en la bandeja. */
export type CasoRegulatorioEncontrado = {
  caso_id: string;
  numero: string | null;
  pais: string;
  nombre_pais: string;
  autoridad: string | null;
  producto: string | null;
  fecha_recepcion: string;
  gravedad: 'grave' | 'no_grave' | null;
  estado_carta: string;
  tramite_concluido: boolean;
  /**
   * Bug #1083 (CC-02, AT S3 §11.3.8): los destinos activos de hoy, en el orden del trámite, y su
   * línea «SENASA · SANIPES», como la bandeja general. `autoridad` es la guardada al abrir el
   * trámite. Opcionales: un servidor anterior no los trae.
   */
  destinos?: string[];
  texto_de_los_destinos?: string | null;
};

/** El trámite tal como lo necesita la pantalla de notificación (`GET /regulatorio/casos/{id}/tramite`). */
export type Tramite = {
  tramite_id: string;
  caso_id: string;
  pais: string;
  autoridad: string | null;
  estado_formato_amarillo: string;
  estado_carta: string;
  estado_cargo: string;
  campos_por_completar: { campo: string; seccion: string; detalle: string; lo_captura_el_contexto_de_casos: boolean }[];
  firmante: string | null;
  presentado_por: string | null;
  fecha_presentacion: string | null;
  firma_solicitada_en: string | null;
  dias_esperando_firma: number | null;
  tramite_concluido: boolean;
  /** HU-24-C12: la remisión del FAR-03 a la autoridad; cumple el segundo reloj. */
  fecha_remision_informe_final: string | null;
  /** HU-20: la carta versionada (C5/C6), su plantilla «SENASA · v1» (C1) y las acciones legales (C3). */
  carta_version: number;
  formato_amarillo_version: number;
  plantilla_usada: string | null;
  carta_desactualizada: boolean;
  acciones_de_la_carta: string[];
  /** HU-45-C5 / HU-20-C5: por qué no se ofrece la firma (campos pendientes o carta desactualizada); null si se puede. */
  razon_de_firma_bloqueada?: string | null;
  versiones_de_la_carta: VersionDeLaCarta[];
  /** HU-21-C3: «Esperando firma del PRFV desde 2026-09-10». */
  texto_de_la_espera: string | null;
  /** HU-21-C1/C2 (RN-05): la ruta de firma la decide la autoridad, no el país. */
  exige_profesional_responsable: boolean | null;
  firmante_responsable_configurado: string | null;
  /** HU-22: el acuse tal como quedó y su etiqueta. */
  cargo: Cargo | null;
  /** BUG-O11: el mismo texto que la fila de la bandeja y el expediente; se pinta tal cual. */
  texto_del_acuse: string | null;
  /** BUG-O11: el mismo hecho como booleano; solo decide el tono de la insignia. */
  acuse_demorado: boolean;
  /** BUG-O11-C2 (AT S3 §2.2.5): «Enviada · v1 · plantilla SENASA v3», armado por el servidor. */
  texto_de_la_carta: string | null;
  // --- CC-01 (AT S3 §10.4.3, `TramiteSalidaCC01`; `ui/MG-ILE-46.md`) ---
  /** «La carta v3 ya está firmada: no se edita ni se reemplaza»; null si se puede o no toca. */
  razon_de_edicion_bloqueada?: string | null;
  /** CC-01-C5: «Editar o adjuntar deja sin efecto la solicitud de firma del …»; null sin espera. */
  texto_del_efecto_en_la_firma?: string | null;
  /** Decisión 11: el tope único de la carta final y del cargo («PDF, hasta 20 MB»). */
  adjunto_de_la_carta?: AdjuntoDeLaCarta | null;
};

/** CC-01 · decisión 11 (CC-01-C16): el tope único por archivo, del parámetro `adjunto_tamano_maximo_mb`. */
export type AdjuntoDeLaCarta = { formatos: string[]; tamano_maximo_mb: number; texto_de_limites: string };

/** HU-22-C1: «Cargo de la autoridad · 2026-09-11». */
export type Cargo = {
  referencia_autoridad: string;
  fecha_recepcion: string;
  etiqueta: string;
  nombre_archivo: string | null;
  tamano_bytes: number | null;
  registrado_por: string | null;
};

/** HU-21-C8: una fila de la cola de firma, con los días hábiles que lleva. */
export type EsperaDeFirma = {
  tramite_id: string;
  caso_id: string;
  numero_de_caso: string;
  pais: string;
  autoridad: string | null;
  solicitada_en: string;
  dias_esperando: number;
  dias_habiles_esperando: number;
  texto: string;
};

export const MENSAJE_CARTA_NO_FIRMADA = 'La carta no está firmada';

/**
 * Como se LEE cada estado de la carta. UN solo mapa: la bandeja, la pantalla de la carta
 * y el expediente tenian cada uno el suyo y decian «Carta preparada, sin firmar» y
 * «Preparada, sin firmar» para lo mismo. Los literales son los que verifican las pruebas
 * («Preparada, sin firmar», «Esperando firma del PRFV», «Enviada»).
 */
export const ESTADOS_DE_LA_CARTA: Record<string, string> = {
  sin_preparar: 'Sin preparar',
  preparada: 'Preparada, sin firmar',
  esperando_firma: 'Esperando firma del PRFV',
  firmada: 'Firmada',
  enviada: 'Enviada',
};

export const etiquetaDeLaCarta = (estado: string | null | undefined): string =>
  (estado ? ESTADOS_DE_LA_CARTA[estado] ?? estado : '—');

/** HU-22-C5 · el cierre del caso: que controles faltan (forma vigente del Sprint 2). */
export type CondicionDeCierre = { condicion: string; detalle: string; control: string | null };

/** HU-27 (AT S3 §2.2.5): una condición del cierre, cumplida o no, con el texto del servidor. */
export type CondicionDelCierre = { control: string; cumplida: boolean; texto: string };

/**
 * `EstadoDelCierreSalidaS3`: el vigente más `condiciones`, TODAS (HU-27-C14). Lo devuelven el
 * GET y el POST del cierre, el registro de una disposición y el cambio de estado de un
 * compromiso. `estado_seguimiento` es `en_seguimiento` o `cerrado`.
 */
export type EstadoDelCierre = {
  caso_id?: string;
  se_puede_cerrar: boolean;
  condiciones_pendientes: CondicionDeCierre[];
  veces_reabierto: number;
  estado_seguimiento: string | null;
  condiciones?: CondicionDelCierre[];
};

/** HU-28 (201 de la reapertura): el cierre, «Reabierto 1 vez» y el aviso del plazo (C8). */
export type ResultadoDeLaReapertura = EstadoDelCierre & {
  texto_de_reaperturas: string;
  advertencia_de_plazo: string | null;
};

/** El 422 `cierre_bloqueado`: la lista completa de lo que falta (HU-27-C14). */
export type CierreBloqueado = { pendientes?: CondicionDeCierre[] };

/** HU-20-C6: una generación de la carta con el texto ya combinado (HU-45). */
export type VersionDeLaCarta = {
  version: number;
  texto: string;
  /** CC-01: nula en la carta adjunta (no sale de una plantilla). */
  plantilla_version: number | null;
  formato_amarillo_version: number;
  campos_pendientes: string[];
  lista_para_firmar: boolean;
  generada_por: string | null;
  generada_en: string;
  es_la_vigente: boolean;
  /**
   * CC-02 · RN-81 (`ui/MG-ILE-1075.md`): «Usa la plantilla de SENASA: falta la de SANIPES» si esta
   * versión se generó con la plantilla de la predeterminada; nulo o ausente si usó la suya.
   */
  texto_de_plantilla_prestada?: string | null;
  // --- CC-01 (`VersionDeLaCartaSalidaCC01`): de dónde salió la versión, quién, cuándo y por qué ---
  origen?: 'generada' | 'editada' | 'adjunta';
  /** «Generada», «Editada», «Adjunta». */
  etiqueta_del_origen?: string;
  /** «Editada por Ana Ríos · 06/10/2026 11:20 · sobre la v2». */
  texto_del_origen?: string | null;
  motivo?: string | null;
  version_base?: number | null;
  /** La carta final adjunta (sin el binario: se descarga aparte). */
  archivo?: { nombre: string; tamano_bytes: number; huella_sha256: string } | null;
  /** `ver_texto` (generada, editada) o `descargar` (adjunta). */
  acciones_permitidas?: string[];
};

/** HU-20-C5, literal del servidor. */
export const MENSAJE_CARTA_DESACTUALIZADA = 'Carta desactualizada: el Formato Amarillo cambió';

/** HU-20-C5: con qué versión del Formato Amarillo se generó la carta vigente (`null` sin carta). */
export const formatoAmarilloDeLaCartaVigente = (tramite: Tramite): number | null =>
  tramite.versiones_de_la_carta.find((v) => v.es_la_vigente)?.formato_amarillo_version ?? null;

/** C5: el texto de cada paso, para el filtro por acción pendiente (C15). */
export const ACCIONES_REGULATORIAS: { value: AccionRegulatoria; label: string }[] = [
  { value: 'completar_formato_amarillo', label: 'Completar el Formato Amarillo' },
  { value: 'preparar_la_carta', label: 'Preparar la carta' },
  { value: 'solicitar_la_firma', label: 'Solicitar la firma' },
  { value: 'esperar_la_firma', label: 'Esperar la firma del PRFV' },
  { value: 'presentar_la_carta', label: 'Presentar la carta' },
  { value: 'registrar_el_cargo', label: 'Registrar el cargo' },
  { value: 'remitir_el_informe_final', label: 'Remitir el informe final' },
];

/**
 * C16: los estados del reloj de notificación por los que se filtra, NOMBRADOS COMO LOS
 * BLOQUES a los que llevan («Vencidos», «Por vencer», «En plazo»), igual que en la bandeja
 * del Comité (HU-11-C13). Los `value` son los que espera el servidor (`estado_plazo`).
 * `cumplido` es la carta ya presentada: son los casos de «Esperando respuesta de afuera» e
 * «Informe final pendiente», y se ofrece con esos nombres.
 */
export const ESTADOS_DEL_RELOJ: { value: EstadoDelReloj; label: string }[] = [
  { value: 'vencido', label: 'Vencidos' },
  { value: 'por_vencer', label: 'Por vencer' },
  { value: 'en_plazo', label: 'En plazo' },
  { value: 'cumplido', label: 'Cumplidos · Esperando respuesta de afuera e Informe final pendiente' },
  { value: 'sin_plazo_configurado', label: 'Sin plazo configurado' },
];

export function consultarBandejaRegulatoria(filtros: FiltrosRegulatorios = {}) {
  const parametros = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor) parametros.set(clave, String(valor).trim());
  }
  const consulta = parametros.toString();
  return pedir<BandejaRegulatoria>(consulta ? `/regulatorio/bandeja?${consulta}` : '/regulatorio/bandeja');
}

/**
 * C20: lo que devuelve «Ver todos los casos». El servidor manda hasta `tope` coincidencias
 * (las de recepción más reciente) y `hay_mas` cuando quedaron otras fuera: la pantalla lo
 * dice en vez de cortar en silencio.
 */
export type BusquedaRegulatoria = {
  casos: CasoRegulatorioEncontrado[];
  hay_mas: boolean;
  tope: number;
};

export function buscarCasoRegulatorio(numero: string) {
  return pedir<BusquedaRegulatoria>(`/regulatorio/bandeja/todos-los-casos?numero=${encodeURIComponent(numero.trim())}`);
}

export function verTramite(casoId: string) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/tramite`);
}

/** HU-21-C1: Perú firma con el responsable configurado y envía en la misma acción. */
export function firmarYEnviar(casoId: string, presentadoPor: string, fechaPresentacion?: string) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/carta/firmar-y-enviar`, {
    metodo: 'POST', cuerpo: { presentado_por: presentadoPor, fecha_presentacion: fechaPresentacion ?? null },
  });
}

/** HU-21-C2/C3: fuera de Perú se solicita la firma del PRFV; la espera es un estado con fecha. */
export function solicitarFirma(casoId: string) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/carta/solicitar-firma`, { metodo: 'POST', cuerpo: {} });
}

/** HU-21-C5: «Firmó: Dr. A. Rojas (PRFV) · 2026-09-14». */
export function registrarFirma(casoId: string, firmante: string, fechaFirma: string) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/carta/firma`, {
    metodo: 'POST', cuerpo: { firmante, fecha_firma: fechaFirma },
  });
}

/** HU-21-C6: presenta OTRA persona que la que firmó. */
export function presentarCarta(casoId: string, presentadoPor: string, fechaPresentacion: string) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/carta/presentacion`, {
    metodo: 'POST', cuerpo: { presentado_por: presentadoPor, fecha_presentacion: fechaPresentacion },
  });
}

/** HU-21-C8: la cola de cartas esperando firma, con los días hábiles que llevan. */
export function colaDeFirma(pais?: string) {
  return pedir<EsperaDeFirma[]>(pais ? `/regulatorio/cola-de-firma?pais=${encodeURIComponent(pais)}` : '/regulatorio/cola-de-firma');
}

/** HU-22-C1: sube el cargo que devolvió la autoridad (base64) con su fecha. */
export function registrarCargo(casoId: string, cuerpo: {
  referencia_autoridad: string; fecha_recepcion: string; nombre_archivo: string; contenido_base64: string;
  tipo_mime?: string | null; registrado_por?: string | null;
}) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/cargo`, { metodo: 'POST', cuerpo });
}

export function descargarCargo(casoId: string) {
  return pedirBinario(`/regulatorio/casos/${segUuid(casoId)}/cargo/archivo`);
}

/** HU-20-C1/C5: prepara la carta con la plantilla vigente; `regenerar` produce la versión siguiente. */
export function prepararCarta(casoId: string, regenerar = false) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/carta`, { metodo: 'POST', cuerpo: { regenerar } });
}

/** HU-22-C5: si el caso se puede cerrar y, si no, que control falta. */
export function estadoDelCierre(casoId: string) {
  return pedir<EstadoDelCierre>(`/regulatorio/casos/${segUuid(casoId)}/cierre`);
}

/**
 * HU-27: cierra el caso. El «cerrado por» sale de la SESIÓN (HU-27-C7, AT S3 §2.0): el cuerpo
 * solo lleva la observación, y mandar `cerrado_por` es 422 (`extra="forbid"`). Un 422
 * `cierre_bloqueado` trae la lista completa de condiciones pendientes en `pendientes`.
 */
export function cerrarCaso(casoId: string, cuerpo: { observacion?: string | null } = {}) {
  return pedir<EstadoDelCierre>(`/regulatorio/casos/${segUuid(casoId)}/cierre`, { metodo: 'POST', cuerpo });
}

/**
 * HU-28: reabre un caso cerrado con motivo (solo el Comité; el autor sale de la sesión). Con
 * `gravedad_nueva` lo reclasifica en el mismo acto y exige `motivo_de_reclasificacion` (C7).
 * 409 si un grave ya notificado intenta bajar a no grave (C9).
 */
export function reabrirCaso(casoId: string, cuerpo: {
  motivo: string;
  gravedad_nueva?: 'grave' | 'no_grave' | null;
  motivo_de_reclasificacion?: string | null;
}) {
  return pedir<ResultadoDeLaReapertura>(`/regulatorio/casos/${segUuid(casoId)}/reapertura`, { metodo: 'POST', cuerpo });
}

// ---- HU-25 · HU-27 · disposiciones y compromisos (AT S3 §2.2.5) ---------------------------

/** HU-25: lo que se registra. La autoridad y el «registrado por» los pone el servidor (D2). */
export type DisposicionNueva = {
  tipo: 'disposicion' | 'solicitud_de_informacion';
  descripcion: string;
  responsable: string;
  /** aaaa-mm-dd */
  plazo: string;
  /** aaaa-mm-dd; la fecha del oficio. */
  fecha?: string | null;
  /**
   * CC-02 (B9, RN-78, CC2-25-C1): la autoridad que la emitió, elegida entre los destinos del
   * caso. Con un solo destino no se manda (el servidor la completa).
   */
  autoridad_codigo?: string | null;
};

/**
 * El 201 del registro (`DisposicionRegistradaSalida`): el estado del cierre del caso (C-08 pasa a
 * pendiente) y el `disposicion_id` que piden las rutas del oficio (HU-25-C7). Nulo con un servidor
 * anterior.
 */
export type DisposicionRegistradaEnElServidor = EstadoDelCierre & { disposicion_id?: string | null };

/** HU-25 / RN-06: registrar es canalizar. */
export function registrarDisposicion(casoId: string, cuerpo: DisposicionNueva) {
  return pedir<DisposicionRegistradaEnElServidor>(`/regulatorio/casos/${segUuid(casoId)}/disposiciones`, { metodo: 'POST', cuerpo });
}

/** HU-25-C7, paso 1 de 3: la URL firmada para subir el oficio (mismo patrón que HU-07). */
export function pedirUrlDelOficio(casoId: string, disposicionId: string, cuerpo: {
  nombre_archivo: string; huella_sha256: string; tamano_bytes: number; contiene_dato_de_personas?: boolean;
}) {
  return pedir<{ url: string; ruta_blob: string; contenedor: string; expira_en: string }>(
    `/regulatorio/casos/${segUuid(casoId)}/disposiciones/${segUuid(disposicionId)}/oficio/url-de-subida`,
    { metodo: 'POST', cuerpo },
  );
}

/** HU-25-C7, paso 3 de 3: el oficio queda en el expediente con su texto de origen. */
export function asociarOficio(casoId: string, disposicionId: string, cuerpo: {
  nombre_archivo: string; ruta_blob: string; huella_sha256: string; tipo_mime: string; tamano_bytes: number;
  contiene_dato_de_personas?: boolean;
}) {
  return pedir<{ disposicion_id: string; adjunto_id: string; texto_de_origen: string }>(
    `/regulatorio/casos/${segUuid(casoId)}/disposiciones/${segUuid(disposicionId)}/oficio`,
    { metodo: 'POST', cuerpo: { ...cuerpo, origen: 'autoridad' } },
  );
}

/** HU-27: una fila de la lista única, con sus textos y sus permisos ya resueltos. */
export type Compromiso = {
  compromiso_id: string;
  caso_id: string;
  numero_de_caso: string;
  /** `accion` o `disposicion`. */
  clase: string;
  descripcion: string;
  responsable: string;
  /** aaaa-mm-dd */
  plazo: string;
  /** `pendiente` · `en_curso` · `cumplida`. */
  estado: string;
  esta_vencido: boolean;
  pais: string;
  /** `accion` · `disposicion` · `solicitud_de_informacion`. */
  tipo: string;
  texto_de_clase: string;
  autoridad: string | null;
  /** CC-02 (B9, RN-78): el código de la autoridad que emitió la disposición. */
  autoridad_codigo?: string | null;
  /** «Registrada por P. Muñoz · Representante del País · CL»; nulo en las acciones del Comité. */
  texto_de_registro: string | null;
  cumplido_en: string | null;
  texto_del_estado: string;
  /** «Vencido hace 9 días»; nulo si no venció. */
  texto_de_vencimiento: string | null;
  puede_cambiar_estado: boolean;
  estados_siguientes: string[];
  puede_revertir: boolean;
  /**
   * ui/MG-ILE-55 · «Cerrar el caso» por fila: `se_puede_cerrar` de `GET .../cierre` para el caso
   * de la fila (un caso cerrado dice `false`). Opcional: un servidor anterior no lo trae.
   */
  caso_puede_cerrarse?: boolean;
};

export type ListaDeCompromisos = {
  abiertos: number;
  vencidos: number;
  /** «Abiertos: 2 · Vencidos: 1». */
  texto_de_contadores: string;
  /** Solo la página pedida (el servidor pagina; de 10 en 10 por defecto). */
  compromisos: Compromiso[];
  /** Filas que pasan los filtros, en todas las páginas. */
  total: number;
  pagina: number;
  por_pagina: number;
};

export type FiltrosDeCompromisos = {
  clase?: 'accion' | 'disposicion' | '';
  estado?: 'pendiente' | 'en_curso' | 'cumplida' | '';
  pais?: string;
  ver_cumplidas?: boolean;
  /** «Vencidos»: solo los abiertos con el plazo pasado (filtra el servidor, antes de paginar). */
  solo_vencidos?: boolean;
  /** «Ver compromisos» del Expediente: solo los de este caso. */
  caso_id?: string;
  pagina?: number;
  por_pagina?: number;
};

/** HU-27: la lista del ámbito del actor, ordenada y paginada por el servidor; las cumplidas, ocultas por defecto. */
export function listarCompromisos(filtros: FiltrosDeCompromisos = {}) {
  const parametros = new URLSearchParams();
  if (filtros.clase) parametros.set('clase', filtros.clase);
  if (filtros.estado) parametros.set('estado', filtros.estado);
  if (filtros.pais) parametros.set('pais', filtros.pais);
  if (filtros.ver_cumplidas) parametros.set('ver_cumplidas', 'true');
  if (filtros.solo_vencidos) parametros.set('solo_vencidos', 'true');
  if (filtros.caso_id) parametros.set('caso_id', filtros.caso_id);
  if (filtros.pagina) parametros.set('pagina', String(filtros.pagina));
  if (filtros.por_pagina) parametros.set('por_pagina', String(filtros.por_pagina));
  const consulta = parametros.toString();
  return pedir<ListaDeCompromisos>(consulta ? `/regulatorio/compromisos?${consulta}` : '/regulatorio/compromisos');
}

/**
 * HU-27-C5 y C17: cambia el estado desde la lista. Revertir una «Cumplida» exige `motivo`.
 * Responde el estado del cierre del caso, no la fila: quien llama refresca la lista (§8.1 R3).
 */
export function cambiarEstadoDeCompromiso(casoId: string, compromisoId: string, cuerpo: { estado: string; motivo?: string | null }) {
  return pedir<EstadoDelCierre>(
    `/regulatorio/casos/${segUuid(casoId)}/compromisos/${segUuid(compromisoId)}/estado`,
    { metodo: 'PATCH', cuerpo },
  );
}

/** HU-24-C12: «FAR-03 remitido a la ANC · fecha». Idempotente: la primera fecha manda. */
export function registrarRemisionDelInformeFinal(casoId: string, fechaRemision: string) {
  return pedir<Tramite>(`/regulatorio/casos/${segUuid(casoId)}/informe-final/remision`, {
    metodo: 'POST', cuerpo: { fecha_remision: fechaRemision },
  });
}

// ---- HU-19 · el Formato Amarillo ---------------------------------------------------------

export type CampoDelFormato = {
  clave: string;
  etiqueta: string;
  valor: string | null;
  /** formato_verde | tramite | evaluacion | sistema */
  origen: string;
  /** C2: la marca «A completar», solo donde falta. */
  a_completar: boolean;
  /** C5: se llena aquí (campo propio del trámite) o se corrige en el caso. */
  editable_aqui: boolean;
  detalle: string | null;
  /**
   * «Animal 1 de 2» en las secciones que se repiten (§4 Producto, §5 Animales,
   * §6 Tratamiento); `null` en las otras once. El Formato Verde ya rotula así sus
   * bloques; la sección NO se duplica, se rotula por dentro.
   */
  bloque?: string | null;
  /** O-08: el valor como se lee (`fuerza_de_ventas` -> «Fuerza de ventas»). `valor` es el código. */
  valor_legible?: string | null;
};

export type SeccionDelFormato = { numero: number; titulo: string; campos: CampoDelFormato[] };

export type VersionDelFormato = {
  version: number;
  generado_por: string | null;
  generado_en: string;
  contenido: { secciones: { numero: number; titulo: string; campos: { clave: string; etiqueta: string; valor: string | null }[] }[] };
};

export type FormatoAmarillo = {
  caso_id: string;
  tramite_id: string;
  numero: string;
  pais: string;
  autoridad: string | null;
  estado: 'pendiente' | 'completo';
  secciones: SeccionDelFormato[];
  campos_por_completar: string[];
  esta_completo: boolean;
  version: number;
  versiones: VersionDelFormato[];
  afecta_a_personas: boolean;
  puede_generar: boolean;
  /** Topes de los textos libres del trámite (`<clave>_caracteres`, parámetro `formato_amarillo_limites_de_texto`). Vacío = sin tope. */
  limites_de_texto?: Record<string, number>;
  /** 21-sep-2026: el Amarillo se congela con la carta firmada o enviada; el motivo lo redacta el servidor. */
  puede_editar?: boolean;
  motivo_de_no_editable?: string | null;
  /** Lo que se ve difiere de la última versión generada (y en qué campos): «Generar de nuevo» solo entonces. */
  hay_cambios_desde_la_ultima_version?: boolean;
  campos_cambiados?: string[];
  /** Dónde va la carta, para avisar antes de generar de nuevo (HU-20-C5). */
  estado_carta?: string;
  carta_version?: number;
  formato_amarillo_version_de_la_carta?: number | null;
  /**
   * CC-02 · RN-79 (`ui/MG-ILE-1075.md`, vacío V-05): las autoridades de las cartas activas. Con más
   * de una, cada versión se imprime con el encabezado de cada autoridad (`?autoridad=`).
   */
  autoridades_destino?: { codigo: string; nombre: string }[];
};

/** El tope de un texto libre del Formato Amarillo, o `undefined` si no está configurado. */
export const topeDelFormato = (formato: Pick<FormatoAmarillo, 'limites_de_texto'> | null | undefined, clave: string): number | undefined => {
  const tope = formato?.limites_de_texto?.[`${clave}_caracteres`];
  return typeof tope === 'number' && tope > 0 ? tope : undefined;
};

/** Los campos propios del trámite (C5). Se guardan sin dar el formato por completo. */
export type DatosDelFormatoAmarillo = {
  tipo_informe?: string | null;
  fecha_informe_inicial?: string | null;
  ref_informe_inicial?: string | null;
  tipo_notificante?: string | null;
  accion_tras_evento?: string | null;
  accion_tras_evento_detalle?: string | null;
  criterio_veterinario?: string | null;
  exposicion_previa?: boolean | null;
  reaccion_previa?: boolean | null;
  reaccion_previa_detalle?: string | null;
  desaparecio_al_suspender?: boolean | null;
  reaparecio_al_readministrar?: boolean | null;
};

const formato = (casoId: string) => `/regulatorio/casos/${segUuid(casoId)}/formato-amarillo`;

export function verFormatoAmarillo(casoId: string) {
  return pedir<FormatoAmarillo>(`${formato(casoId)}/documento`);
}

export function guardarFormatoAmarillo(casoId: string, datos: DatosDelFormatoAmarillo) {
  return pedir<Tramite>(formato(casoId), { metodo: 'PUT', cuerpo: datos });
}

/** C4/C6: nace «Formato Amarillo vN»; regenerar es la siguiente versión. */
export function generarFormatoAmarillo(casoId: string) {
  return pedir<FormatoAmarillo>(`${formato(casoId)}/generar`, { metodo: 'POST' });
}

/** C3: la fecha de término se captura EN EL CASO (contexto de casos), no en el trámite. */
export function fijarFechaDeTerminoDelEvento(casoId: string, fechaTermino: string) {
  return pedir<{ caso_id: string; fecha_termino: string }>(`/casos/${segUuid(casoId)}/evento-adverso/fecha-termino`, {
    metodo: 'PUT', cuerpo: { fecha_termino: fechaTermino },
  });
}

/**
 * O-08 (QA, 28-sep-2026): lo que el Formato Amarillo pide del caso y el Formato Verde no
 * captura —«Serie» (§4), «Vía y lugar» y «Según rótulo» (§6, por producto) y «Seguimiento
 * cronológico» (§8)—. PARCIAL: lo que no viene no se toca; `null` lo borra. El producto va
 * por su `orden`, que es el número de bloque del Amarillo («Medicamento 2 de 3»).
 */
export type DatosDelCasoParaElFormato = {
  seguimiento_cronologico?: string | null;
  medicamentos?: { orden: number; serie?: string | null; via_y_lugar?: string | null; segun_rotulo?: boolean | null }[];
};

export type DatosDelCasoParaElFormatoGuardados = {
  caso_id: string;
  version_bloqueo: number;
  campos_cambiados: string[];
  ya_estaban: boolean;
};

/** O-08: como la fecha de término, se captura EN EL CASO (contexto de casos), no en el trámite. */
export function completarDatosDelFormatoAmarillo(casoId: string, datos: DatosDelCasoParaElFormato) {
  return pedir<DatosDelCasoParaElFormatoGuardados>(`/casos/${segUuid(casoId)}/datos-del-formato-amarillo`, {
    metodo: 'PATCH', cuerpo: datos,
  });
}

/**
 * C4: «Imprimir produce solo el documento»: el HTML de impresión de una versión. Se pide por el
 * cliente, con el token, y no con un enlace: la navegación de un enlace sale sin `Authorization`
 * y con Entra la API responde 401 (BUG-1090). Lo abre en otra pestaña `abrirDocumentoEnPestana`.
 */
export function imprimirFormatoAmarillo(casoId: string, version: number) {
  return pedirBinario(`${formato(casoId)}/versiones/${version}/imprimir`);
}
